import React, { useState, useRef, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Tester por texto do agente: conversa no navegador (sem ligar), exercitando
// persona + ferramentas + saída. O backend roda o loop de tools (gpt-4o).
const useStyles = makeStyles(theme => ({
  paper: { height: "80vh", maxHeight: 680 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 8 },
  hint: { fontSize: 11.5, color: theme.app.textTertiary, padding: "0 24px 8px" },
  body: { flex: 1, overflowY: "auto", padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8, background: theme.app.chatBg },
  row: { display: "flex" },
  user: { justifyContent: "flex-end" },
  bot: { justifyContent: "flex-start" },
  bubble: { maxWidth: "78%", padding: "8px 12px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  bUser: { background: theme.app.dark, color: "#fff", borderBottomRightRadius: 4 },
  bBot: { background: "#fff", color: theme.app.ink, border: `1px solid ${theme.app.border}`, borderBottomLeftRadius: 4 },
  trace: { alignSelf: "center", fontFamily: "monospace", fontSize: 11, color: theme.app.textSecondary, background: theme.app.lineSoft, borderRadius: 6, padding: "3px 8px", maxWidth: "90%", overflow: "hidden", textOverflow: "ellipsis" },
  inputRow: { display: "flex", gap: 8, alignItems: "center", padding: "10px 16px", borderTop: `1px solid ${theme.app.border}`, background: "#fff" },
  empty: { margin: "auto", color: theme.app.textTertiary, fontSize: 13, textAlign: "center" },
}));

const fmt = v => (v == null ? "" : typeof v === "string" ? v : JSON.stringify(v));

const AgentChatTester = ({ open, onClose, agentId, beforeSend }) => {
  const classes = useStyles();
  const [display, setDisplay] = useState([]); // {kind:"user"|"bot"|"trace", text}
  const [msgs, setMsgs] = useState([]); // opaco (formato OpenAI) p/ o backend
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDisplay([]);
      setMsgs([]);
      setInput("");
    }
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [display, busy]);

  const traceLine = t => {
    if (t.kind === "end") return `✓ encerrou${t.result && Object.keys(t.result).length ? ` · resultado ${fmt(t.result)}` : ""}`;
    if (t.kind === "transfer") return `→ transferiria para humano (${(t.result && t.result.to) || ""})`;
    return `🔧 ${t.name}(${fmt(t.args)}) → ${fmt(t.result)}`;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setDisplay(d => [...d, { kind: "user", text }]);
    setBusy(true);
    try {
      if (beforeSend) await beforeSend(); // salva o agente p/ testar a config atual
      const { data } = await api.post(`/applications/${agentId}/agent-chat`, { messages: msgs, text });
      setMsgs(data.messages || []);
      const add = [];
      (data.trace || []).forEach(t => add.push({ kind: "trace", text: traceLine(t) }));
      if (data.reply) add.push({ kind: "bot", text: data.reply });
      if (data.ended) add.push({ kind: "trace", text: "— conversa encerrada pelo agente —" });
      setDisplay(d => [...d, ...add]);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <div className={classes.head}>
        <DialogTitle>Conversar com o agente (teste)</DialogTitle>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </div>
      <div className={classes.hint}>
        Prévia por texto (raciocínio em gpt-4o). Na ligação real vale o provedor/voz configurado. Salva o agente a cada envio.
      </div>
      <div className={classes.body} ref={bodyRef}>
        {display.length === 0 && !busy && (
          <div className={classes.empty}>Mande uma mensagem para começar a conversa com o agente.</div>
        )}
        {display.map((m, i) =>
          m.kind === "trace" ? (
            <div key={i} className={classes.trace}>{m.text}</div>
          ) : (
            <div key={i} className={`${classes.row} ${m.kind === "user" ? classes.user : classes.bot}`}>
              <div className={`${classes.bubble} ${m.kind === "user" ? classes.bUser : classes.bBot}`}>{m.text}</div>
            </div>
          )
        )}
        {busy && (
          <div className={`${classes.row} ${classes.bot}`}>
            <div className={`${classes.bubble} ${classes.bBot}`}><CircularProgress size={14} /></div>
          </div>
        )}
      </div>
      <div className={classes.inputRow}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Escreva como se fosse o cliente…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={busy}
          autoFocus
        />
        <IconButton color="primary" onClick={send} disabled={busy || !input.trim()}>
          <SendIcon />
        </IconButton>
      </div>
    </Dialog>
  );
};

export default AgentChatTester;
